import { useQuery, useMutation } from '@apollo/client';
import type { RenderParams } from '@seek/forms-ui';
import { ContractsApi } from '@seek/sbiz-shared-types';
import { required } from '@seek/validators-js';
import {
  Stack,
  Columns,
  Column,
  Card,
  Inline,
  Box,
  Button,
  ContentBlock,
  useResponsiveValue,
  Loader,
  Text,
  useToast,
  Divider,
  TextLink,
} from 'braid-design-system';
import queryString from 'query-string';
import React, {
  Fragment,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import ConditionalWrapper from 'src/components/ConditionalWrapper/ConditionalWrapper';
import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import Routes from 'src/config/routes';
import { useCookiesStrict } from 'src/hooks/cookies';
import type { User } from 'src/types/user';
import {
  fireOnboardingCompleteEvent,
  fireOnboardingCompleteSemEvent,
  fireRegistrationEvent,
} from 'src/utils/analytics';
import { clearSelectedProduct } from 'src/utils/cookies';
import { hasConsented } from 'src/utils/validators';

import BusinessDetails from './components/BusinessDetails/BusinessDetails';
import { EmailOptIn } from './components/EmailOptIn/EmailOptIn';
import PhoneNumberDetails from './components/PhoneNumber/PhoneNumber';
import UserName from './components/UserName/UserName';
import UserType from './components/UserType/UserType';
import ONBOARD_USER from './queries/onboardUser';
import USER_DETAILS from './queries/userDetails';
import {
  FormProvider,
  isEmailConsentRequired,
  mapImAToUserRegistrationType,
  type OnboardingFormData,
  useField,
} from './utils';

const OnboardingForm = ({ handleSubmit }: RenderParams<OnboardingFormData>) => {
  const urlLocation = useLocation();
  const { cookies, setCookie, removeCookie } = useCookiesStrict([
    'SelectedProduct',
    'OnboardPageSource',
    'HasVisitedOnboard',
    'HasOnboarded',
  ]);
  const { returnUrl } = queryString.parseUrl(urlLocation.search).query;
  const stringUrl = returnUrl as string;
  const [onboardUser] = useMutation(ONBOARD_USER);
  const [onboardingInProgress, setOnboardingInProgress] = useState(false);
  const showToast = useToast();
  const onboardInProgressRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (cookies.OnboardPageSource && !cookies.HasVisitedOnboard) {
      setCookie('HasVisitedOnboard', true);
      // registrationCompleteSem event
      fireRegistrationEvent('Sem', 'Complete');
    }
  }, [cookies.OnboardPageSource, cookies.HasVisitedOnboard, setCookie]);

  const submit = async (formData: OnboardingFormData) => {
    setOnboardingInProgress(true);

    onboardInProgressRef.current?.scrollIntoView({ block: 'end' });

    const input = {
      ...formData,
      emailOptIn: formData.emailOptIn ?? false,
    };

    try {
      await onboardUser({ variables: { input } });
    } catch {
      showToast({
        tone: 'critical',
        message:
          'There was an unexpected issue. Please try again or contact our support.',
        action: {
          label: 'Contact us',
          onClick: () => window.location.assign('/contact-us'),
        },
        data: { testid: 'onboarding-error' },
      });
      setOnboardingInProgress(false);
      return {};
    }
    await fireOnboardingCompleteEvent(formData.userRegistrationType);

    if (cookies.OnboardPageSource && !cookies.HasOnboarded) {
      await fireOnboardingCompleteSemEvent(formData.userRegistrationType);
      setCookie('HasOnboarded', true);
      removeCookie('OnboardPageSource');
    }

    if (returnUrl) {
      window?.location.assign(stringUrl);
    } else if (
      cookies.SelectedProduct === ContractsApi.ProductIds.PayPerEnquiry
    ) {
      clearSelectedProduct();
      window.location.assign(Routes.PAY_PER_ENQUIRY_CONTRACT);
    } else if (cookies.SelectedProduct === ContractsApi.ProductIds.Freemium) {
      clearSelectedProduct();
      window.location.assign(Routes.FREEMIUM_CONTRACT);
    } else if (formData.userRegistrationType === 'PrivateSeller') {
      let checkoutUrl: string = Routes.DASHBOARD;
      if (cookies.SelectedProduct) {
        checkoutUrl = queryString.stringifyUrl({
          url: Routes.PAYMENT,
          query: { productToken: cookies.SelectedProduct as string },
        });
      }
      window?.location.assign(checkoutUrl);
    } else if (formData.userRegistrationType === 'Buyer') {
      window?.location.assign('/profile/saved-searches/');
    } else {
      window?.location.assign('/sign-up/advertiser/');
    }

    return {};
  };

  const userRegistrationType = useField({
    id: 'userRegistrationType',
    validators: [required],
  });

  // WARNING: we influence the ".valid" property of this field by the below "isEmailOptInUseFieldValid"
  // We do this as the form's internal state doesn't reevaluate "validatorProps"
  // when dependent values change leading to stale validation messages
  const emailOptInUseField = useField({
    id: 'emailOptIn',
    validators: [hasConsented],
    validatorProps: {
      userRegistrationType: userRegistrationType.value,
    },
  });
  const isEmailOptInFieldValid = isEmailConsentRequired(
    userRegistrationType.value,
  )
    ? emailOptInUseField.valid
    : true;

  const responsiveValue = useResponsiveValue();
  const isNotMobile =
    responsiveValue({
      mobile: false,
      tablet: true,
      desktop: true,
    }) ?? false;

  if (onboardingInProgress) {
    return (
      <Fragment>
        <ContentBlock>
          <Box
            ref={onboardInProgressRef}
            paddingY="large"
            paddingX="large"
            style={{ minHeight: 500 }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            background="surface"
            marginY="small"
          >
            <Stack space="medium" align="center">
              <Loader />
              <Text>Creating your SEEK Business profile</Text>
            </Stack>
          </Box>
        </ContentBlock>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Header title="Onboarding" />
      <ContentBlock>
        <Box paddingY="large" paddingX="large">
          <Card>
            <form onSubmit={handleSubmit(submit)}>
              <ConditionalWrapper
                condition={isNotMobile}
                wrapper={(childNode: ReactNode) => (
                  <Columns space="none">
                    <Column width="3/5">{childNode}</Column>
                  </Columns>
                )}
              >
                <Stack space="large">
                  <UserName />
                  <UserType
                    valid={userRegistrationType.valid}
                    errorMessage={userRegistrationType.errorMessage}
                    onChange={userRegistrationType.onChange}
                    value={userRegistrationType.value}
                  />
                  {userRegistrationType.value === 'PrivateSeller' && (
                    <BusinessDetails />
                  )}
                  {userRegistrationType.value !== 'PrivateSeller' &&
                    userRegistrationType.value !== 'Buyer' && (
                      <PhoneNumberDetails />
                    )}
                  <Stack space="medium">
                    <Divider />
                    <EmailOptIn
                      userRegistrationType={userRegistrationType.value}
                      valid={isEmailOptInFieldValid}
                      errorMessage={emailOptInUseField.errorMessage}
                      onChange={emailOptInUseField.onChange}
                      value={emailOptInUseField.value ?? false}
                    />
                  </Stack>

                  <Stack space="gutter">
                    <Inline space="gutter">
                      <Text
                        data={{
                          testid:
                            'onboarding-privacy-policy-and-terms-and-conditions',
                        }}
                      >
                        By clicking on Continue you agree to accept the SEEK
                        Business{' '}
                        <TextLink
                          href="https://www.seekbusiness.com.au/privacy-policy"
                          target="_blank"
                        >
                          Privacy Policy
                        </TextLink>
                        {' and '}
                        <TextLink
                          href="https://www.seekbusiness.com.au/terms-and-conditions"
                          target="_blank"
                        >
                          Terms & Conditions
                        </TextLink>
                        .
                      </Text>
                    </Inline>

                    <Inline space="medium" collapseBelow="tablet">
                      <Button
                        type="submit"
                        data={{
                          htag: 'onboarding-continue-cta',
                          analytics: 'onboarding-continue-cta',
                        }}
                      >
                        Continue
                      </Button>
                    </Inline>
                  </Stack>
                </Stack>
              </ConditionalWrapper>
            </form>
          </Card>
        </Box>
      </ContentBlock>
    </Fragment>
  );
};

const Onboarding = () => {
  const urlLocation = useLocation();
  const { ima } = queryString.parseUrl(urlLocation.search).query;
  const { data, loading } = useQuery<User>(USER_DETAILS);
  const emailOptIn =
    data?.user?.subscription?.subscriptionStatus === 'Subscribed';
  const initialValues = {
    firstName: data?.user.firstName ?? '',
    lastName: data?.user.lastName ?? '',
    userRegistrationType: mapImAToUserRegistrationType(ima as string),
    businessName: undefined,
    abn: undefined,
    phoneNumber: undefined,
    phoneCountryCode: '61',
    emailOptIn,
  };
  return (
    <Fragment>
      {loading ? (
        <CustomLoader />
      ) : (
        <FormProvider initialValues={initialValues}>
          {(props) => <OnboardingForm {...props} />}
        </FormProvider>
      )}
    </Fragment>
  );
};

export default Onboarding;
